import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth.jsx";
import api from "../api/index.js";
import SEO from "../components/SEO.jsx";

const G = {
  bg:"#0A0A0A", surface:"#111111", border:"#1E1E1E",
  text:"#E4E4DC", muted:"#666", accent:"#00C896",
  purple:"#7C6FFF", danger:"#FF6B6B", warn:"#FFB347",
  font:"'Sora', sans-serif", mono:"'DM Mono', monospace",
};

const StatCard = ({ label, value, color=G.accent, icon }) => (
  <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, padding:"20px 24px" }}>
    <div style={{ fontSize:28, marginBottom:8 }}>{icon}</div>
    <div style={{ fontSize:28, fontWeight:700, color, fontFamily:G.mono }}>{value}</div>
    <div style={{ fontSize:12, color:G.muted, marginTop:4 }}>{label}</div>
  </div>
);

const Btn = ({ children, onClick, variant="primary", small, disabled }) => {
  const variants = {
    primary: { background:G.accent, color:"#000" },
    ghost:   { background:"#1A1A1A", color:G.muted, border:`1px solid #2A2A2A` },
    danger:  { background:G.danger+"22", color:G.danger, border:`1px solid ${G.danger}44` },
    warn:    { background:G.warn+"22", color:G.warn, border:`1px solid ${G.warn}44` },
    success: { background:G.accent+"22", color:G.accent, border:`1px solid ${G.accent}44` },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{ border:"none", cursor:disabled?"default":"pointer", fontFamily:G.mono, fontWeight:500, borderRadius:20, padding:small?"4px 12px":"8px 18px", fontSize:small?11:13, opacity:disabled?0.4:1, transition:"opacity 0.15s", ...variants[variant] }}
      onMouseOver={e => { if(!disabled) e.currentTarget.style.opacity="0.8"; }}
      onMouseOut={e => { e.currentTarget.style.opacity=disabled?"0.4":"1"; }}>
      {children}
    </button>
  );
};

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab]               = useState("overview");
  const [stats, setStats]           = useState(null);
  const [users, setUsers]           = useState([]);
  const [questions, setQuestions]   = useState([]);
  const [listings, setListings]     = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [qSearch, setQSearch]       = useState("");
  const [lSearch, setLSearch]       = useState("");
  const [toast, setToast]           = useState(null);
  const [loading, setLoading]       = useState(true);

  const notify = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),2500); };

  useEffect(() => {
    if (!user) return navigate("/login");
    if (!user.is_admin) return navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    setLoading(true);
    if (tab === "overview") api.get("/admin/stats").then(r => setStats(r.data.stats)).catch(()=>{}).finally(()=>setLoading(false));
    if (tab === "users") api.get("/admin/users").then(r => setUsers(r.data.users)).catch(()=>{}).finally(()=>setLoading(false));
    if (tab === "questions") api.get("/admin/questions").then(r => setQuestions(r.data.questions)).catch(()=>{}).finally(()=>setLoading(false));
    if (tab === "listings") api.get("/listings?limit=50").then(r => setListings(r.data.listings)).catch(()=>{}).finally(()=>setLoading(false));
  }, [tab]);

  const handleBan = async (id, username, isBanned) => {
    if (!window.confirm(`${isBanned?"Unban":"Ban"} ${username}?`)) return;
    try {
      const r = await api.put(`/admin/users/${id}/ban`);
      setUsers(us => us.map(u => u.id===id?{...u,is_banned:r.data.user.is_banned}:u));
      notify(`${r.data.user.is_banned?"Banned":"Unbanned"} ${username}`);
    } catch { notify("Failed","error"); }
  };

  const handlePromote = async (id, username, isAdmin) => {
    if (!window.confirm(`${isAdmin?"Remove admin from":"Make admin"} ${username}?`)) return;
    try {
      const r = await api.put(`/admin/users/${id}/promote`);
      setUsers(us => us.map(u => u.id===id?{...u,is_admin:r.data.user.is_admin}:u));
      notify(`${r.data.user.is_admin?"Promoted":"Demoted"} ${username}`);
    } catch { notify("Failed","error"); }
  };

  const handleDeleteQ = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try { await api.delete(`/admin/questions/${id}`); setQuestions(qs=>qs.filter(q=>q.id!==id)); notify("Deleted"); }
    catch { notify("Failed","error"); }
  };

  const handleCloseQ = async (id) => {
    try {
      const r = await api.put(`/admin/questions/${id}/close`);
      setQuestions(qs=>qs.map(q=>q.id===id?{...q,is_closed:r.data.question.is_closed}:q));
      notify(r.data.question.is_closed?"Closed":"Reopened");
    } catch { notify("Failed","error"); }
  };

  const handleVerifyL = async (id) => {
    try {
      const r = await api.put(`/listings/${id}/verify`);
      setListings(ls=>ls.map(l=>l.id===id?{...l,is_verified:r.data.listing.is_verified}:l));
      notify(r.data.listing.is_verified?"Listing verified":"Verification removed");
    } catch { notify("Failed","error"); }
  };

  const handleFlagL = async (id) => {
    try {
      const r = await api.put(`/listings/${id}/flag`);
      setListings(ls=>ls.map(l=>l.id===id?{...l,is_flagged:r.data.listing.is_flagged}:l));
      notify(r.data.listing.is_flagged?"Listing flagged":"Flag removed");
    } catch { notify("Failed","error"); }
  };

  const searchUsers = async () => {
    try { const r = await api.get(`/admin/users?search=${userSearch}`); setUsers(r.data.users); } catch {}
  };

  const searchQuestions = async () => {
    try { const r = await api.get(`/admin/questions?search=${qSearch}`); setQuestions(r.data.questions); } catch {}
  };

  const searchListings = async () => {
    try { const r = await api.get(`/listings?search=${lSearch}&limit=50`); setListings(r.data.listings); } catch {}
  };

  const tabs = ["overview","users","questions","listings"];

  return (
    <div style={{ maxWidth:1100, margin:"0 auto", padding:"28px 16px", fontFamily:G.font }}>
      <SEO title="Admin Dashboard" noindex />
      {toast && <div style={{ position:"fixed", top:20, right:20, zIndex:9999, background:toast.type==="error"?G.danger:G.accent, color:toast.type==="error"?"#fff":"#000", padding:"11px 20px", borderRadius:8, fontFamily:G.mono, fontSize:13, fontWeight:600 }}>{toast.msg}</div>}

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24 }}>
        <div>
          <div style={{ fontSize:11, color:G.accent, fontFamily:G.mono, letterSpacing:2, textTransform:"uppercase", marginBottom:4 }}>Admin</div>
          <h1 style={{ fontSize:26, fontWeight:700, color:G.text, margin:0 }}>FOGRA Dashboard</h1>
        </div>
        <Link to="/" style={{ textDecoration:"none", color:G.muted, fontSize:13, fontFamily:G.mono }}>← Back to FOGRA</Link>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:4, marginBottom:24, background:G.surface, border:`1px solid ${G.border}`, borderRadius:10, padding:4, width:"fit-content" }}>
        {tabs.map(t => (
          <button key={t} onClick={() => { setTab(t); setLoading(true); }} style={{
            background:tab===t?"#1A1A1A":"transparent",
            border:tab===t?`1px solid #2A2A2A`:"1px solid transparent",
            color:tab===t?G.accent:G.muted,
            padding:"6px 18px", borderRadius:7, cursor:"pointer",
            fontSize:13, fontFamily:G.mono, textTransform:"capitalize", transition:"all 0.15s"
          }}>{t}</button>
        ))}
      </div>

      {/* Overview */}
      {tab==="overview" && (
        loading ? <div style={{ textAlign:"center", padding:"60px 0", color:G.muted }}>Loading...</div>
        : stats ? (
          <div>
            <div className="admin-stats" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:16, marginBottom:32 }}>
              <StatCard label="Total Users"     value={stats.users}     color={G.accent}  icon="👥" />
              <StatCard label="Total Questions" value={stats.questions} color={G.purple}  icon="❓" />
              <StatCard label="Total Answers"   value={stats.answers}   color={G.warn}    icon="💬" />
              <StatCard label="Total Comments"  value={stats.comments}  color="#4ECDC4"   icon="🗨️" />
            </div>
            <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, padding:24 }}>
              <div style={{ fontSize:11, color:G.muted, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase", marginBottom:16 }}>Quick Actions</div>
              <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
                <Btn onClick={() => setTab("users")}>👥 Manage Users</Btn>
                <Btn onClick={() => setTab("questions")} variant="ghost">❓ Manage Questions</Btn>
                <Btn onClick={() => setTab("listings")} variant="ghost">🏢 Manage Listings</Btn>
                  <Btn onClick={() => navigate("/admin/analytics")} variant="ghost">📊 Analytics</Btn>
              </div>
            </div>
          </div>
        ) : <div style={{ textAlign:"center", padding:"60px 0", color:G.danger }}>Failed to load</div>
      )}

      {/* Users */}
      {tab==="users" && (
        <div>
          <div style={{ display:"flex", gap:10, marginBottom:16 }}>
            <input placeholder="Search users..." value={userSearch} onChange={e => setUserSearch(e.target.value)}
              onKeyDown={e => e.key==="Enter"&&searchUsers()}
              style={{ flex:1, background:"#141414", border:`1px solid #2A2A2A`, color:G.text, borderRadius:8, padding:"9px 14px", fontSize:13, fontFamily:G.font, outline:"none" }} />
            <Btn onClick={searchUsers}>Search</Btn>
          </div>
          <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, overflow:"hidden" }}>
            <div style={{ display:"grid", gridTemplateColumns:"2fr 2fr 1fr 1fr 1fr auto", padding:"10px 16px", borderBottom:`1px solid ${G.border}`, fontSize:10, color:G.muted, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase" }}>
              <span>Username</span><span>Email</span><span>Questions</span><span>Answers</span><span>Status</span><span>Actions</span>
            </div>
            {users.map(u => (
              <div key={u.id} style={{ display:"grid", gridTemplateColumns:"2fr 2fr 1fr 1fr 1fr auto", padding:"12px 16px", borderBottom:`1px solid #0F0F0F`, alignItems:"center" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:13, color:G.text, fontWeight:500 }}>{u.username}</span>
                  {u.is_admin && <span style={{ background:G.accent+"22", color:G.accent, fontSize:9, padding:"1px 6px", borderRadius:10, fontFamily:G.mono }}>ADMIN</span>}
                </div>
                <span style={{ fontSize:12, color:G.muted }}>{u.email}</span>
                <span style={{ fontSize:13, color:G.text, fontFamily:G.mono }}>{u.question_count}</span>
                <span style={{ fontSize:13, color:G.text, fontFamily:G.mono }}>{u.answer_count}</span>
                <span style={{ fontSize:11, color:u.is_banned?G.danger:G.accent, fontFamily:G.mono }}>{u.is_banned?"Banned":"Active"}</span>
                <div style={{ display:"flex", gap:6 }}>
                  <Btn small variant={u.is_banned?"warn":"danger"} onClick={() => handleBan(u.id,u.username,u.is_banned)}>{u.is_banned?"Unban":"Ban"}</Btn>
                  <Btn small variant="ghost" onClick={() => handlePromote(u.id,u.username,u.is_admin)}>{u.is_admin?"Demote":"Admin"}</Btn>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Questions */}
      {tab==="questions" && (
        <div>
          <div style={{ display:"flex", gap:10, marginBottom:16 }}>
            <input placeholder="Search questions..." value={qSearch} onChange={e => setQSearch(e.target.value)}
              onKeyDown={e => e.key==="Enter"&&searchQuestions()}
              style={{ flex:1, background:"#141414", border:`1px solid #2A2A2A`, color:G.text, borderRadius:8, padding:"9px 14px", fontSize:13, fontFamily:G.font, outline:"none" }} />
            <Btn onClick={searchQuestions}>Search</Btn>
          </div>
          <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, overflow:"hidden" }}>
            <div style={{ display:"grid", gridTemplateColumns:"3fr 1fr 1fr 1fr auto", padding:"10px 16px", borderBottom:`1px solid ${G.border}`, fontSize:10, color:G.muted, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase" }}>
              <span>Title</span><span>Author</span><span>Answers</span><span>Status</span><span>Actions</span>
            </div>
            {questions.map(q => (
              <div key={q.id} style={{ display:"grid", gridTemplateColumns:"3fr 1fr 1fr 1fr auto", padding:"12px 16px", borderBottom:`1px solid #0F0F0F`, alignItems:"center" }}>
                <Link to={`/q/${q.slug}`} style={{ textDecoration:"none" }}>
                  <span style={{ fontSize:13, color:G.text, fontWeight:500, display:"-webkit-box", WebkitLineClamp:1, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{q.title}</span>
                </Link>
                <span style={{ fontSize:12, color:G.muted }}>{q.author_username}</span>
                <span style={{ fontSize:13, color:G.text, fontFamily:G.mono }}>{q.answer_count}</span>
                <span style={{ fontSize:11, color:q.is_closed?G.danger:G.accent, fontFamily:G.mono }}>{q.is_closed?"Closed":"Open"}</span>
                <div style={{ display:"flex", gap:6 }}>
                  <Btn small variant={q.is_closed?"warn":"ghost"} onClick={() => handleCloseQ(q.id)}>{q.is_closed?"Open":"Close"}</Btn>
                  <Btn small variant="danger" onClick={() => handleDeleteQ(q.id,q.title)}>Delete</Btn>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Listings */}
      {tab==="listings" && (
        <div>
          <div style={{ display:"flex", gap:10, marginBottom:16 }}>
            <input placeholder="Search listings..." value={lSearch} onChange={e => setLSearch(e.target.value)}
              onKeyDown={e => e.key==="Enter"&&searchListings()}
              style={{ flex:1, background:"#141414", border:`1px solid #2A2A2A`, color:G.text, borderRadius:8, padding:"9px 14px", fontSize:13, fontFamily:G.font, outline:"none" }} />
            <Btn onClick={searchListings}>Search</Btn>
          </div>
          <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, overflow:"hidden" }}>
            <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr auto", padding:"10px 16px", borderBottom:`1px solid ${G.border}`, fontSize:10, color:G.muted, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase" }}>
              <span>Name</span><span>Category</span><span>Reviews</span><span>Verified</span><span>Flagged</span><span>Actions</span>
            </div>
            {loading ? (
              <div style={{ textAlign:"center", padding:"40px 0", color:G.muted }}>Loading...</div>
            ) : listings.map(l => (
              <div key={l.id} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr auto", padding:"12px 16px", borderBottom:`1px solid #0F0F0F`, alignItems:"center" }}>
                <Link to={`/listings/${l.slug}`} style={{ textDecoration:"none" }}>
                  <span style={{ fontSize:13, color:G.text, fontWeight:500 }}>{l.name}</span>
                </Link>
                <span style={{ fontSize:12, color:G.muted }}>{l.category}</span>
                <span style={{ fontSize:13, color:G.text, fontFamily:G.mono }}>{l.review_count}</span>
                <span style={{ fontSize:11, color:l.is_verified?G.accent:G.muted, fontFamily:G.mono }}>{l.is_verified?"✓ Yes":"No"}</span>
                <span style={{ fontSize:11, color:l.is_flagged?G.danger:G.muted, fontFamily:G.mono }}>{l.is_flagged?"⚠ Yes":"No"}</span>
                <div style={{ display:"flex", gap:6 }}>
                  <Btn small variant={l.is_verified?"ghost":"success"} onClick={() => handleVerifyL(l.id)}>{l.is_verified?"Unverify":"Verify"}</Btn>
                  <Btn small variant={l.is_flagged?"warn":"danger"} onClick={() => handleFlagL(l.id)}>{l.is_flagged?"Unflag":"Flag"}</Btn>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
